/**
 * Web Audio API ile procedural ses efektleri.
 *
 * Hiçbir ses dosyası gerektirmez — tarayıcıda sentezlenir.
 * AudioContext tarayıcı autoplay politikası nedeniyle SADECE kullanıcı
 * ilk etkileşiminden sonra başlatılabilir; ilk tıklamada init edilir.
 *
 * Kullanıcı tercihi ``localStorage``'da saklanır.
 */

const STORAGE_KEY = 'mulakid:sound-enabled';

type SoundName = 'pop' | 'chime' | 'fanfare' | 'boing' | 'sparkle' | 'whoosh';

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let enabled = true;

function readPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

function writePref(v: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
  } catch {
    /* yut */
  }
}

export function isSoundEnabled(): boolean {
  return enabled;
}

export function setSoundEnabled(v: boolean) {
  enabled = v;
  writePref(v);
  if (masterGain) {
    masterGain.gain.value = v ? 0.5 : 0;
  }
}

export function toggleSound(): boolean {
  setSoundEnabled(!enabled);
  return enabled;
}

function ensureCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  }
  const Ctor =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = enabled ? 0.5 : 0;
    masterGain.connect(ctx.destination);
    return ctx;
  } catch {
    return null;
  }
}

/**
 * İlk kullanıcı etkileşiminde çağır — tarayıcı autoplay iznini verir.
 */
export function unlockAudio() {
  const c = ensureCtx();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
}

// ---- Synth primitives -----------------------------------------------------

function envBlip(
  freq: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume = 0.3,
  sweepTo?: number,
) {
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (sweepTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), t + duration);
  }
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(volume, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(t);
  osc.stop(t + duration + 0.02);
}

function envNoise(duration: number, volume = 0.15, freq = 1200, q = 1.5) {
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t = c.currentTime;
  const bufferSize = Math.floor(c.sampleRate * duration);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const source = c.createBufferSource();
  source.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = q;
  const gain = c.createGain();
  gain.gain.value = volume;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(masterGain);
  source.start(t);
  source.stop(t + duration + 0.02);
}

// ---- Public sounds --------------------------------------------------------

export function playPop() {
  // Kısa, yüksek "pop" — tıklama sticker burst'u için
  const c = ensureCtx();
  if (!c) return;
  const variation = 0.85 + Math.random() * 0.3;
  envBlip(900 * variation, 0.08, 'triangle', 0.25, 600 * variation);
}

export function playSparkle() {
  // İnce, parlak "tink" — hover ve küçük etkileşimler
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1800, t);
  osc.frequency.exponentialRampToValueAtTime(2400, t + 0.1);
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.18, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.2);
}

export function playChime() {
  // 3 notadan oluşan çan — kompliman / Konami için
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
  notes.forEach((freq, i) => {
    setTimeout(() => envBlip(freq, 0.5, 'sine', 0.22, freq * 0.9), i * 90);
  });
}

export function playFanfare() {
  // Parti modu açılışı — kısa parlak fanfar
  const notes = [
    { f: 523.25, d: 0, t: 0.12 },
    { f: 659.25, d: 100, t: 0.12 },
    { f: 783.99, d: 200, t: 0.12 },
    { f: 1046.5, d: 300, t: 0.45 },
    { f: 783.99, d: 700, t: 0.15 },
    { f: 1046.5, d: 800, t: 0.4 },
  ];
  notes.forEach(({ f, d, t }) => {
    setTimeout(() => envBlip(f, t, 'triangle', 0.25, f * 0.95), d);
  });
}

export function playBoing() {
  // Hızlı "boing" — CTA click
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(220, t);
  osc.frequency.exponentialRampToValueAtTime(880, t + 0.08);
  osc.frequency.exponentialRampToValueAtTime(330, t + 0.18);
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.3, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.32);
}

export function playWhoosh() {
  // Hışırtı — sayfa geçişleri
  envNoise(0.35, 0.12, 800, 2);
}

export function play(name: SoundName) {
  switch (name) {
    case 'pop':      return playPop();
    case 'chime':    return playChime();
    case 'fanfare':  return playFanfare();
    case 'boing':    return playBoing();
    case 'sparkle':  return playSparkle();
    case 'whoosh':   return playWhoosh();
  }
}

// ---- Party music (procedural loop) ----------------------------------------

/**
 * 128 BPM'lik çocuk dostu parti loop'u. Davul + bas + majör akor arpeji.
 * startPartyMusic() çağırın, durdurmak için dönen controller.stop() kullan.
 *
 * Schedule pattern: Web Audio'nun hassas zamanlaması için
 * ``currentTime`` kullanır — ``setInterval`` jitter'sız çalar.
 */

type PartyController = { stop: () => void };

function midiToFreq(midi: number) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function scheduleKick(c: AudioContext, t: number, out: AudioNode) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.frequency.setValueAtTime(150, t);
  osc.frequency.exponentialRampToValueAtTime(40, t + 0.12);
  gain.gain.setValueAtTime(0.45, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
  osc.connect(gain);
  gain.connect(out);
  osc.start(t);
  osc.stop(t + 0.2);
}

function scheduleSnare(c: AudioContext, t: number, out: AudioNode) {
  const bufferSize = Math.floor(c.sampleRate * 0.15);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    const env = Math.pow(1 - i / bufferSize, 2);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  const source = c.createBufferSource();
  source.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 1500;
  const gain = c.createGain();
  gain.gain.value = 0.25;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(out);
  source.start(t);
  source.stop(t + 0.2);
}

function scheduleHat(c: AudioContext, t: number, out: AudioNode, accent = false) {
  const bufferSize = Math.floor(c.sampleRate * 0.04);
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 3);
  }
  const source = c.createBufferSource();
  source.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 7000;
  const gain = c.createGain();
  gain.gain.value = accent ? 0.12 : 0.06;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(out);
  source.start(t);
  source.stop(t + 0.05);
}

function scheduleBass(c: AudioContext, t: number, midi: number, out: AudioNode) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(midiToFreq(midi), t);
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(180, t);
  filter.frequency.exponentialRampToValueAtTime(80, t + 0.18);
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.35, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(out);
  osc.start(t);
  osc.stop(t + 0.22);
}

function scheduleChord(c: AudioContext, t: number, midis: number[], out: AudioNode, dur: number) {
  midis.forEach((m, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(midiToFreq(m), t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.12, t + 0.02 + i * 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain);
    gain.connect(out);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  });
}

function scheduleMelody(c: AudioContext, t: number, midi: number, out: AudioNode, dur: number) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(midiToFreq(midi), t);
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(2000, t);
  filter.frequency.exponentialRampToValueAtTime(800, t + dur);
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.13, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(out);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

let activeParty: PartyController | null = null;

export function startPartyMusic(): PartyController | null {
  if (activeParty) return activeParty;
  const c = ensureCtx();
  if (!c || !masterGain) return null;

  const musicGain = c.createGain();
  musicGain.gain.value = 0;
  musicGain.gain.linearRampToValueAtTime(0.45, c.currentTime + 0.4);
  musicGain.connect(masterGain);

  // Basit, çocuk dostu C-am aralığında loop. 128 BPM, 16 step = 1 bar.
  const tempo = 128;
  const stepDur = 60 / tempo / 2; // 8th notes
  const pattern = 16;

  // Her step için: [kick?, snare?, hat?, bassMidi?, chordMidis?, melodyMidi?]
  // Majör hissi için C major / F major / G major akorları
  const seq: Array<{
    k?: boolean; s?: boolean; h?: boolean;
    b?: number; ch?: number[]; m?: number; md?: number;
  }> = [
    { k: true,  h: true,  b: 36, ch: [60, 64, 67], m: 72, md: 0.3 }, // C
    { h: true,                                          m: 76, md: 0.2 },
    { h: true,  b: 36,                                   m: 79, md: 0.2 },
    { h: true,                                          m: 76, md: 0.2 },
    { s: true,  h: true,  b: 41, ch: [65, 69, 72], m: 72, md: 0.3 }, // F
    { h: true,                                          m: 77, md: 0.2 },
    { h: true,  b: 41,                                   m: 81, md: 0.2 },
    { h: true,                                          m: 77, md: 0.2 },
    { k: true,  h: true,  b: 43, ch: [67, 71, 74], m: 74, md: 0.3 }, // G
    { h: true,                                          m: 79, md: 0.2 },
    { h: true,  b: 43,                                   m: 81, md: 0.2 },
    { h: true,                                          m: 79, md: 0.2 },
    { s: true,  h: true,  b: 36, ch: [60, 64, 67], m: 72, md: 0.3 }, // C
    { h: true,                                          m: 76, md: 0.2 },
    { h: true,  b: 41,                                   m: 79, md: 0.2 },
    { s: true,  h: true,                                 m: 72, md: 0.4 },
  ];

  let stepIdx = 0;
  let nextTime = c.currentTime + 0.05;

  const scheduler = (window as unknown as {
    setInterval: (fn: () => void, ms: number) => number;
  }).setInterval(() => {
    if (!activeParty) return;
    const lookahead = c.currentTime + 0.15;
    while (nextTime < lookahead) {
      const step = seq[stepIdx];
      if (step.k) scheduleKick(c, nextTime, musicGain);
      if (step.s) scheduleSnare(c, nextTime, musicGain);
      if (step.h) scheduleHat(c, nextTime, musicGain, stepIdx % 4 === 0);
      if (step.b !== undefined) scheduleBass(c, nextTime, step.b, musicGain);
      if (step.ch) scheduleChord(c, nextTime, step.ch, musicGain, stepDur * 4 * 0.95);
      if (step.m !== undefined && step.md !== undefined)
        scheduleMelody(c, nextTime, step.m, musicGain, step.md);
      nextTime += stepDur;
      stepIdx = (stepIdx + 1) % pattern;
    }
  }, 25);

  activeParty = {
    stop: () => {
      const stopTime = c.currentTime + 0.05;
      musicGain.gain.cancelScheduledValues(stopTime);
      musicGain.gain.setTargetAtTime(0, stopTime, 0.08);
      (window as unknown as { clearInterval: (id: number) => void }).clearInterval(scheduler);
      (window as unknown as { setTimeout: (fn: () => void, ms: number) => number }).setTimeout(
        () => musicGain.disconnect(),
        400,
      );
      activeParty = null;
    },
  };
  return activeParty;
}

export function stopPartyMusic() {
  activeParty?.stop();
}

// Kullanıcı tercihini init sırasında oku
enabled = readPref();